import React, { useState, useEffect } from 'react';
import styles from './TimePicker.module.css';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  required?: boolean;
  disabled?: boolean;
}

interface TimeParts {
  hour: string;
  minute: string;
  period: string;
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, required = false, disabled = false }) => {
  // Parse value (HH:MM format) to hours, minutes, and period
  const parseTime = (timeString: string): TimeParts => {
    if (!timeString) return { hour: '09', minute: '00', period: 'AM' };

    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours, 10);

    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';

    return {
      hour: String(hour12).padStart(2, '0'),
      minute: minutes || '00',
      period: period,
    };
  };

  const [timeParts, setTimeParts] = useState<TimeParts>(() => parseTime(value));

  // Update when value prop changes
  useEffect(() => {
    setTimeParts(parseTime(value));
  }, [value]);

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 1;
    return String(hour).padStart(2, '0');
  });

  // Generate minute options (00, 15, 30, 45) - common intervals for scheduling
  const minuteOptions = ['00', '15', '30', '45'];

  // If existing minute is not in our list, add it as an option
  const getMinuteOptions = () => {
    if (timeParts.minute && !minuteOptions.includes(timeParts.minute)) {
      return [...minuteOptions, timeParts.minute].sort();
    }
    return minuteOptions;
  };

  const handleChange = (field: keyof TimeParts, newValue: string) => {
    const updated = { ...timeParts, [field]: newValue };
    setTimeParts(updated);

    // Convert to 24-hour format (HH:MM)
    let hour24 = parseInt(updated.hour, 10);
    if (updated.period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (updated.period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }

    const timeString = `${String(hour24).padStart(2, '0')}:${updated.minute}`;
    onChange(timeString);
  };

  return (
    <div className={styles.timePicker}>
      <select
        className={styles.timeSelect}
        value={timeParts.hour}
        onChange={(e) => handleChange('hour', e.target.value)}
        required={required}
        disabled={disabled}
      >
        {hourOptions.map((hour) => (
          <option key={hour} value={hour}>
            {hour}
          </option>
        ))}
      </select>
      <span className={styles.timeSeparator}>:</span>
      <select
        className={styles.timeSelect}
        value={timeParts.minute}
        onChange={(e) => handleChange('minute', e.target.value)}
        required={required}
        disabled={disabled}
      >
        {getMinuteOptions().map((minute) => (
          <option key={minute} value={minute}>
            {minute}
          </option>
        ))}
      </select>
      <select
        className={styles.timeSelect}
        value={timeParts.period}
        onChange={(e) => handleChange('period', e.target.value)}
        required={required}
        disabled={disabled}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

export default TimePicker;
